import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "../../components/dashboard/DashboardLayout";
import { PageHeader, StatusBadge } from "../../components/dashboard/UI";
import { useI18n } from "../../i18n/I18nContext";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import {
  FolderKanban, KeyRound, LifeBuoy, FolderLock, Bell,
  ArrowRight, Activity, Zap,
} from "lucide-react";

const Overview = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recentNotifs, setRecentNotifs] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, n, k] = await Promise.all([
        api.get("/dashboard/summary").then((r) => r.data).catch(() => null),
        api.get("/notifications").then((r) => r.data.slice(0, 4)).catch(() => []),
        api.get("/tickets").then((r) => r.data.slice(0, 4)).catch(() => []),
      ]);
      setSummary(s);
      setRecentNotifs(n);
      setRecentTickets(k);
    })();
  }, []);

  const cards = summary && [
    { id: "projects", icon: FolderKanban, label: t("dashboard.activeProjects"),
      value: summary.projects.active, total: summary.projects.total, to: "/dashboard/projects" },
    { id: "licenses", icon: KeyRound, label: t("dashboard.totalLicenses"),
      value: summary.licenses.total, total: summary.licenses.expiring, totalLabel: t("dashboard.expiringLicenses"), to: "/dashboard/licenses" },
    { id: "tickets", icon: LifeBuoy, label: t("dashboard.openTickets"),
      value: summary.tickets.open, total: summary.tickets.critical, totalLabel: t("dashboard.criticalTickets"), to: "/dashboard/tickets" },
    { id: "notifs", icon: Bell, label: t("dashboard.unreadNotifications"),
      value: summary.notifications.unread, total: summary.documents.total, totalLabel: t("dashboard.totalDocuments"), to: "/dashboard/notifications" },
  ];

  return (
    <DashboardLayout>
      <PageHeader
        eyebrow={t("dashboard.summary")}
        title={`${t("dashboard.welcome")}, ${user?.name?.split(" ")[0] || ""}`}
        subtitle={user?.company_name}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards
          ? cards.map((c, i) => {
              const Icon = c.icon;
              return (
                <Link
                  key={c.id}
                  to={c.to}
                  data-testid={`overview-card-${c.id}`}
                  className={`card-sharp p-6 group reveal reveal-${i + 1}`}
                >
                  <div className="flex items-start justify-between">
                    <Icon className="w-5 h-5 text-[#19A7E0]" />
                    <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#19A7E0] transition-colors" />
                  </div>
                  <p className="mt-6 text-4xl font-medium tracking-tight font-mono">{c.value}</p>
                  <p className="mt-2 tactical-label">{c.label}</p>
                  {c.total !== undefined && c.totalLabel && (
                    <p className="mt-3 text-xs text-white/50 font-mono">
                      {c.totalLabel}: <span className="text-[#F59E0B]">{c.total}</span>
                    </p>
                  )}
                  {c.total !== undefined && !c.totalLabel && (
                    <p className="mt-3 text-xs text-white/40 font-mono">/ {c.total} totales</p>
                  )}
                </Link>
              );
            })
          : Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card-sharp p-6 h-40 animate-pulse" />
            ))}
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="card-sharp p-6" data-testid="recent-activity">
          <div className="flex items-center justify-between mb-5">
            <p className="tactical-label flex items-center gap-2"><Activity className="w-3 h-3" /> {t("dashboard.recentActivity")}</p>
            <Link to="/dashboard/notifications" className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#19A7E0]">
              {t("dashboard.viewAll")}
            </Link>
          </div>
          <ul className="space-y-3">
            {recentNotifs.length === 0 && <li className="text-sm text-white/50">{t("dashboard.noData")}</li>}
            {recentNotifs.map((n) => (
              <li key={n.id} className="flex items-start gap-3 py-3 border-b border-white/[0.06] last:border-0" data-testid={`recent-notif-${n.id}`}>
                <span className={`status-dot mt-1.5 ${n.read ? "dot-muted" : "dot-accent"}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium">{n.title}</p>
                  <p className="text-xs text-white/50 mt-1 line-clamp-2">{n.body}</p>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30 mt-2">
                    {t(`notifTypes.${n.type}`)} · {new Date(n.created_at).toLocaleString()}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="card-sharp p-6" data-testid="recent-tickets">
          <div className="flex items-center justify-between mb-5">
            <p className="tactical-label flex items-center gap-2"><Zap className="w-3 h-3" /> {t("dashboard.tickets")}</p>
            <Link to="/dashboard/tickets" className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#19A7E0]">
              {t("dashboard.viewAll")}
            </Link>
          </div>
          <ul className="space-y-3">
            {recentTickets.length === 0 && <li className="text-sm text-white/50">{t("dashboard.noData")}</li>}
            {recentTickets.map((tk) => (
              <li key={tk.id} className="flex items-start gap-3 py-3 border-b border-white/[0.06] last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-[10px] font-mono text-[#19A7E0]">{tk.code}</p>
                    <StatusBadge status={tk.priority} label={t(`priorities.${tk.priority}`)} />
                  </div>
                  <p className="text-sm font-medium mt-1.5 truncate">{tk.title}</p>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mt-1.5">
                    {t(`statuses.${tk.status}`)} · {tk.category}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </DashboardLayout>
  );
};

export default Overview;
